/**
 * Correlation ids for interaction tracking.
 *
 * One id per USER ACTION, shared by every event that action produces. A save that changes three
 * plan cards is one intent, not three; a lesson opened and then abandoned is one reading session,
 * not two unrelated rows. Without this the server can only see the rows, never the shape of what
 * the person actually did — which is the gap InteractionEvents.CorrelationId was added to close.
 *
 * Scope is the ACTION, not the session and not the screen. Holding one id for a whole visit would
 * group everything into a single blob and lose exactly the distinction it exists to draw.
 */
export function newCorrelationId(): string {
  // crypto.randomUUID needs a secure context. Localhost qualifies and production is HTTPS, but a
  // plain-http host would leave it undefined — hence the fallback rather than a bare call.
  const cryptoRef = typeof crypto !== "undefined" ? (crypto as Crypto & { randomUUID?: () => string }) : undefined;

  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID();
  }

  if (cryptoRef?.getRandomValues) {
    const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
    // RFC 4122 v4: version in the high nibble of byte 6, variant in the top bits of byte 8.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex: string[] = [];
    for (let i = 0; i < bytes.length; i++) {
      hex.push(bytes[i].toString(16).padStart(2, "0"));
    }
    return (
      `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-` +
      `${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`
    );
  }

  // No crypto at all. Math.random is not a defensible source of unique ids in general, but the
  // only consequence of a collision here is two unrelated actions being grouped in an analytics
  // view — and the alternative, sending nothing, loses the grouping outright.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
